import { useEffect, useState } from 'react';
import { Cloud, CloudOff, Check, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Saving indicator component for Auto-Save visual feedback
 * Shows "Saving...", "Saved Xm ago", or error state. Relative time updates
 * every 30s so the badge doesn't get stale.
 */
export function SavingIndicator({ isSaving, lastSaved, error, className }) {
  // Tick every 30s so the relative label stays fresh without a parent re-render
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSaved) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [lastSaved]);

  const formatRelative = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    const diffMs = Date.now() - dateObj.getTime();
    const diffSec = Math.round(diffMs / 1000);
    if (diffSec < 10) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (error) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium",
        "bg-red-50 text-red-600 border border-red-200",
        className
      )}>
        <CloudOff className="w-3.5 h-3.5" />
        <span>Save failed</span>
      </div>
    );
  }

  if (isSaving) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium",
        "bg-blue-50 text-blue-600 border border-blue-200 animate-pulse",
        className
      )} data-testid="saving-indicator">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Saving…</span>
      </div>
    );
  }

  if (lastSaved) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium",
        "bg-emerald-50 text-emerald-700 border border-emerald-200",
        className
      )} data-testid="saved-indicator" title={new Date(lastSaved).toLocaleString()}>
        <Check className="w-3.5 h-3.5" />
        <span>Saved {formatRelative(lastSaved)}</span>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium",
      "bg-slate-50 text-slate-500 border border-slate-200",
      className
    )}>
      <Cloud className="w-3.5 h-3.5" />
      <span>Auto-save on</span>
    </div>
  );
}

export default SavingIndicator;
